<template>
  <GridHolder class="header-margin page-search">
    <div class="page-search__search-input">
      <DefaultInput
        id="page-search-input"
        ref="pageSearchInput"
        v-model="searchTerm"
        :value="searchTerm"
        type="search"
        autofocus
        placeholder="Wonach suchen Sie?"
        @input.native="doSearch"
        @keyup.13.native="doSearch"
      />
      <Transition name="fade-500">
        <ClearInput v-if="searchTerm" @clear-input-field="clearSearch()" />
      </Transition>
    </div>
    <Transition name="fade-300" mode="out-in">
      <LoadSkeleton v-if="loading && searchTerm.length >= 3" key="skeleton" class="page-search__load-skeleton" />
      <div v-else-if="articles" key="results">
        <h2 v-if="resultCount" class="page-search__result-count">{{ resultCount }}</h2>
        <ul v-if="articles.length > 0" class="page-search__results">
          <li v-for="article in articles" :key="article.id" class="page-search__results-teaser">
            <SearchResultTeaser :article="article" />
          </li>
        </ul>
      </div>
    </Transition>
  </GridHolder>
</template>

<script>
import SEARCH from '@/gql/search'
import GridHolder from '@/components/ui/grid/grid'
import DefaultInput from '@/components/ui/form/input/default'
import LoadSkeleton from '@/components/ui/loader/skeleton-teaser'
import ClearInput from '@/components/ui/form/input/clear-input'
import SearchResultTeaser from '@/components/ui/teaser/horizontal'

const DEBOUNCE_TIME = 300
let searchTimeout = null

export default {
  components: {
    GridHolder,
    DefaultInput,
    ClearInput,
    SearchResultTeaser,
    LoadSkeleton,
  },
  data() {
    return {
      searchTerm: '',
      loading: false,
      articles: null,
    }
  },
  computed: {
    resultCount() {
      if (this.searchTerm.length < 3) {
        return null
      }

      return this.articles && this.articles.length > 0
        ? `${this.articles.length} Artikel zu "${this.searchTerm}" gefunden.`
        : 'Zu diesem Begriff wurde nichts gefunden.'
    },
  },
  mounted() {
    this.$refs.pageSearchInput.$el.focus()
    if (this.$route.query.q) {
      this.searchTerm = this.$route.query.q
      this.doSearch()
    }
  },
  methods: {
    doSearch() {
      if (this.searchTerm.length < 3) {
        this.articles = null
        this.loading = false
        this.$router.push({ path: this.$route.path, query: {} })
        return
      }

      this.loading = true
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(async () => {
        const { data } = await this.$apolloProvider.defaultClient.query({
          query: SEARCH,
          variables: {
            portalId: this.$config.portalId,
            filter_search: this.searchTerm,
          },
        })

        this.articles = data.getArticlesByPortalId.data
        this.loading = false
        this.$router.push({ path: this.$route.path, query: { q: this.searchTerm } })
      }, DEBOUNCE_TIME)
    },
    clearSearch() {
      this.articles = null
      this.loading = false
      this.searchTerm = ''
      this.$router.push({ path: this.$route.path, query: {} })
    },
  },
}
</script>

<style lang="scss">
@import '../css/import/vars';

.page-search {
  padding: 30px 20px 0;
  margin: 0 auto;
  max-width: 700px;

  @media all and (min-width: $screen-width-767) {
    padding: 50px 20px 0;
  }

  &__search-input {
    margin-bottom: 2rem;
    position: relative;

    &:after {
      content: ' ';
      background-image: url('~assets/svg/icon-magnifier.svg');
      width: 17px;
      height: 16px;
      position: absolute;
      right: 10px;
      top: 0;
      bottom: 0;
      margin: auto;
    }

    input {
      padding-right: 3.2rem;
    }
  }

  &__result-count {
    font-size: 16px;
    margin-bottom: 2rem;

    @media all and (min-width: $screen-width-767) {
      font-size: 18px;
    }
  }

  &__load-skeleton {
    height: 540px;
  }

  &__results {
    list-style-type: none;

    &-teaser {
      margin-bottom: 1rem;
      padding-left: 0;
      padding-bottom: 1rem;
      border-bottom: 1px solid $color-gray;

      &:last-child {
        border-bottom: none;
      }

      &::before {
        display: none;
      }
    }
  }
}
</style>
